import { Suspense, lazy, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import SessionProvider from './providers/SessionProvider'
import AuthenticatedShell from './AuthenticatedShell'
import {
  RedirectIfSignedIn,
  RequireActiveOrganisation,
  RequireAuth,
  RequireFinancialAccess,
  RequirePlatformAdmin,
  RequireRole,
} from './guards/RouteGuards'
import LoginPage from '../features/auth/LoginPage'
import { useSession } from './providers/sessionContext'
import { AuthLoading, PermissionDeniedPage, SessionExpired } from '../features/auth/AccessStatePages'

/**
 * Lazy routes.
 *
 * Split by destination rather than by component, because that is the boundary a
 * user actually crosses. Login, the guards and every access state stay eager:
 * they are what a signed-out or blocked person sees, and making them wait on a
 * chunk would mean a spinner for people who are not going anywhere.
 *
 * The guards run before Suspense, so a chunk never begins loading for a route
 * the user may not have, and protected content cannot appear while it loads.
 */
const StartInvitePage = lazy(() => import('../features/auth/StartInvitePage'))
const LiveDashboardPage = lazy(() => import('../features/dashboard/LiveDashboardPage'))
const OperationalDashboardPage = lazy(
  () => import('../features/dashboard/OperationalDashboardPage'),
)
const JobsReportPage = lazy(() => import('../features/reports/JobsReportPage'))
const EmployeesReportPage = lazy(() => import('../features/reports/EmployeesReportPage'))
const OvertimeReportPage = lazy(() => import('../features/reports/OvertimeReportPage'))
const UnallocatedReportPage = lazy(() => import('../features/reports/UnallocatedReportPage'))
const ScheduleOutcomesReportPage = lazy(
  () => import('../features/reports/ScheduleOutcomesReportPage'),
)
const ReportDetailPage = lazy(() => import('../features/reports/ReportDetailPage'))
const ExceptionsReportPage = lazy(() => import('../features/reports/ExceptionsReportPage'))
const MembersPage = lazy(() => import('../features/admin/MembersPage'))
const EmployeesPage = lazy(() => import('../features/settings/EmployeesPage'))
const CustomersPage = lazy(() => import('../features/customers/CustomersPage'))
const JobsPage = lazy(() => import('../features/jobs/JobsPage'))
const ProductsPage = lazy(() => import('../features/products/ProductsPage'))
const ProductDetailPage = lazy(() => import('../features/products/ProductDetailPage'))
const TimesheetsPage = lazy(() => import('../features/timesheets/TimesheetsPage'))
const SchedulerPage = lazy(() => import('../features/scheduler/SchedulerPage'))
const JobScheduleTab = lazy(() => import('../features/scheduler/JobScheduleTab'))
const JobIntelligenceTab = lazy(() => import('../features/intelligence/JobIntelligenceTab'))
const RosterPage = lazy(() => import('../features/roster/RosterPage'))
const RosterPatternsPage = lazy(() => import('../features/roster/RosterPatternsPage'))
const WorkforcePage = lazy(() => import('../features/settings/WorkforcePage'))
const JobDetailPage = lazy(() => import('../features/jobs/JobDetailPage'))
const FulfilmentRecordPage = lazy(() => import('../features/jobs/FulfilmentRecordPage'))
const CustomerDetailPage = lazy(() => import('../features/customers/CustomerDetailPage'))
const JobStatusesPage = lazy(() => import('../features/settings/JobStatusesPage'))
const OutcomeReasonsPage = lazy(() => import('../features/settings/OutcomeReasonsPage'))
const JobCategoriesPage = lazy(() => import('../features/settings/JobCategoriesPage'))
const OrganisationSettingsPage = lazy(
  () => import('../features/settings/OrganisationSettingsPage'),
)
const OvertimeRulesPage = lazy(() => import('../features/settings/OvertimeRulesPage'))
const BillingRulesPage = lazy(() => import('../features/settings/BillingRulesPage'))
const PublicHolidaysPage = lazy(() => import('../features/settings/PublicHolidaysPage'))
const WageMatrixPage = lazy(() => import('../features/settings/WageMatrixPage'))
const AuditHistoryPage = lazy(() => import('../features/settings/AuditHistoryPage'))
const ReadinessPage = lazy(() => import('../features/settings/ReadinessPage'))
const ImportBatchesPage = lazy(() => import('../features/import/ImportBatchesPage'))
const MigrationsPage = lazy(() => import('../features/migration/MigrationsPage'))
const MigrationWorkspacePage = lazy(() => import('../features/migration/MigrationWorkspacePage'))
const ImportUploadPage = lazy(() => import('../features/import/ImportUploadPage'))
const ImportMappingPage = lazy(() => import('../features/import/ImportMappingPage'))
const ImportReviewPage = lazy(() => import('../features/import/ImportReviewPage'))
const ImportConfirmPage = lazy(() => import('../features/import/ImportConfirmPage'))
const CostingPage = lazy(() => import('../features/costing/CostingPage'))
const CostingDetailPage = lazy(() => import('../features/costing/CostingDetailPage'))
const PlatformOrganisationsPage = lazy(
  () => import('../features/platform/PlatformOrganisationsPage'),
)
const SetPasswordPage = lazy(() =>
  import('../features/auth/PasswordPages').then((m) => ({ default: m.SetPasswordPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('../features/auth/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage })),
)
const RecoverPasswordPage = lazy(() =>
  import('../features/auth/PasswordPages').then((m) => ({ default: m.RecoverPasswordPage })),
)

/**
 * Wraps a lazy route in the same loading state session resolution uses, so a
 * chunk fetch and an auth check look identical to the person waiting.
 */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<AuthLoading />}>{children}</Suspense>
}

/**
 * The route table.
 *
 * Guards wrap routes rather than living inside pages, so a screen is protected
 * by where it is mounted rather than by somebody remembering to add a check.
 * A missing guard then looks like a route sitting outside the guarded block in
 * this one file, which is something review can actually catch.
 *
 * The placeholder screens exist so Phase 3a can prove the guards work against
 * real routes. They are replaced by the real ones in 3b and 3c.
 */

/**
 * Which dashboard this account gets.
 *
 * The decision is made here, before either chunk is fetched and before any
 * query is sent, because the two dashboards are two different data paths rather
 * than one screen with fields hidden. An account with financial access gets the
 * costed report; an account without gets the operational one, which never asks
 * for a wage table at all. Choosing after loading would mean the restricted
 * figures had already been fetched, which is the thing ADR-004 exists to
 * prevent.
 */
function DashboardForRole() {
  const session = useSession()
  return session.canViewFinancials ? (
    <Lazy><LiveDashboardPage /></Lazy>
  ) : (
    <Lazy><OperationalDashboardPage /></Lazy>
  )
}

/*
 * The Phase 3a `Placeholder` component was removed at M9 Session 2B. It stood
 * in for screens that were guarded before they were built, and every route it
 * covered now has a real one. Keeping it would have left a component whose
 * whole message is "this is not built yet" in a codebase where that is no
 * longer true of any route.
 */

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginPage />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/session-expired" element={<SessionExpired />} />

      {/* The scanner-safe invitation route. Deliberately outside every guard:
          the recipient is not signed in, and opening it must be harmless. It is
          also reachable while signed in, so somebody already using the product
          sees the correct state rather than a redirect. */}
      <Route path="/start-invite/:token" element={<Lazy><StartInvitePage /></Lazy>} />

      {/* Recovery. Public by necessity, and exposes no application content. */}
      <Route path="/forgot-password" element={<Lazy><ForgotPasswordPage /></Lazy>} />
      <Route path="/recover-password" element={<Lazy><RecoverPasswordPage /></Lazy>} />

      {/* Reached with a live session from a one-time code, so it needs a signed
          in user but not a membership: an invited owner sets their password
          before anything else exists. */}
      <Route path="/set-password" element={<Lazy><SetPasswordPage /></Lazy>} />

      {/* The dashboard. Live from M9: it reads confirmed timesheet entries and
          reports on them, replacing the M2 design fixtures that stood here
          while there was no engine to ask.

          Deliberately not behind RequireFinancialAccess. This is where every
          signed-in person lands, and bouncing somebody to the denied page would
          make the product look broken rather than restricted. From Session 2
          the permission chooses between two dashboards rather than between a
          dashboard and a notice: financial access gets the costed report,
          everyone else gets the operational one, which never asks for a wage
          table. The choice happens before either chunk loads. ADR-004. */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <AuthenticatedShell>
              <DashboardForRole />
            </AuthenticatedShell>
          </RequireAuth>
        }
      />

      {/* Management reports.

          Deliberately behind RequireAuth and RequireActiveOrganisation only,
          not behind RequireFinancialAccess. Each report chooses its own data
          path from the permission, exactly as the dashboard does: an account
          with financial access gets the costed report, an account without gets
          the operational one, and the choice is made before anything is
          requested. Guarding the routes financially would lock operational
          users out of hours reporting they are entitled to see.

          Overtime is the exception and is guarded, because overtime cannot be
          calculated without the restricted pay rate at all (ADR-080). It is
          guarded inside the page rather than by RequireFinancialAccess so the
          refusal can explain the reason, which is a property of the
          calculation rather than a bare permission denial. */}
      <Route
        path="/reports/jobs"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><JobsReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/reports/jobs/:jobId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ReportDetailPage subject="job" /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/reports/employees"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><EmployeesReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/reports/employees/:employeeId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ReportDetailPage subject="employee" /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/reports/overtime"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><OvertimeReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      {/* Costing exceptions. Financial by nature: every row names missing pay
          or charge-out configuration for a named employee, so the list itself
          describes financial settings. Refused inside the page like overtime,
          so the refusal can say what an exception actually is. */}
      <Route
        path="/reports/exceptions"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ExceptionsReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/reports/unallocated"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><UnallocatedReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* M16B. Operational: what happened to scheduled work. No wage, cost or
          margin figure exists on this path, so it is guarded exactly as the
          other operational reports are and needs no financial permission. */}
      <Route
        path="/reports/schedule-outcomes"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ScheduleOutcomesReportPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* Financial data. Mirrors can_view_financials; the policy still decides.
          The wage matrix moved under /settings at M6C, next to the rest of the
          configuration screens; this keeps the older path working. */}
      <Route path="/wage-matrix" element={<Navigate to="/settings/wage-matrix" replace />} />
      {/* The M3 placeholder promised employee costs "at milestone M9". M9
          Session 2B built it, so the old path lands on the real report rather
          than on a screen still saying it does not exist. */}
      <Route path="/employee-costs" element={<Navigate to="/reports/employees" replace />} />

      {/* Administration. Owner or admin. */}
      <Route
        path="/admin/users"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><MembersPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Employees. Deliberately not behind RequireRole or
          RequireFinancialAccess.

          Who works here is not financial data, and a viewer has a legitimate
          reason to see the roster. The two sensitive things inside are guarded
          where they actually live: pay amounts by RLS on employee_pay_rates,
          which returns nothing to somebody without financial access, and every
          write by the owner-or-admin policies. The screen reflects both rather
          than deciding either, so a permission change needs no code change. */}
      <Route
        path="/settings/employees"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><EmployeesPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* The scheduler. Guarded like the other operational screens: who is
          working when is operational rather than financial, so a viewer reads
          it, and every write is refused by the owner-or-admin policies on the
          scheduling tables. Nothing financial appears here at all. */}
      <Route
        path="/scheduler"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><SchedulerPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* The roster: when people are expected to be at work.

          Operational, so a viewer reads it, exactly like the scheduler. Every
          write is refused by the owner-or-admin policies on the roster tables,
          and nothing financial appears here at all.

          A separate route from /scheduler because they answer separate
          questions, and one route from which rostering can be entirely absent:
          an organisation that never rosters sees the screen's own empty state
          and the scheduler continues to work from working hours. */}
      <Route
        path="/roster"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><RosterPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* Roster patterns and who is on them. Configuration of the roster rather
          than the roster itself, so owner or admin, and reached from the roster
          rather than from Settings: it is the same person's job. */}
      <Route
        path="/roster/patterns"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><RosterPatternsPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Workforce configuration: working hours, unavailability and skills.
          Owner or admin, matching every other configuration screen. */}
      <Route
        path="/settings/workforce"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><WorkforcePage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Jobs, and one job. The operational screens, distinct from
          /reports/jobs which answers what a job cost.

          Guarded like the customer screens: hours and who worked them are
          operational, so a viewer reads them. The financial half of the job
          page is decided inside the page from can_view_financials, and that
          account never calls the costing service at all, so there is no
          request through which a wage could arrive (ADR-080). Every write is
          refused by the owner-or-admin policies. */}
      <Route
        path="/jobs"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><JobsPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* M17. The product library, guarded exactly as jobs are: **a member
          reads it** because a worker has to open the drawing their job was
          ordered against, and only an owner or administrator writes, which the
          policies decide rather than the route. Nothing here is financial. */}
      <Route
        path="/products"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ProductsPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/products/:productId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><ProductDetailPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                {/* The composition root joins the two features: job detail
                    owns the tabs, the scheduler owns the scheduling. */}
                <Lazy>
                  <JobDetailPage
                    renderSchedule={(context) => (
                      <Lazy>
                        <JobScheduleTab {...context} />
                      </Lazy>
                    )}
                    /* Same composition as the schedule slot, and for the same
                       reason: the intelligence is its own feature, features may
                       not import one another, and a second copy of the
                       statistics inside job detail would give the product two
                       answers to how long work takes. */
                    renderIntelligence={(context) => (
                      <Lazy>
                        <JobIntelligenceTab {...context} />
                      </Lazy>
                    )}
                  />
                </Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* One delivery or collection record, as a document to print or hand
          over.

          Outside the application shell on purpose: it opens in its own tab
          from the job and exists to be printed, so a sidebar and a navigation
          header would be furniture on somebody's docket. Guarded exactly like
          the job it belongs to, and the policies decide what it can read
          (M18). */}
      <Route
        path="/jobs/:jobId/fulfilment/:eventId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <Lazy>
                <FulfilmentRecordPage />
              </Lazy>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* Customers, and one customer.

          Guarded at the same level as the employee roster and for the same
          reason: who the customer is is operational rather than financial, and
          a viewer needs to read it to make sense of a job. Every write is
          refused by the owner-or-admin policies on `customers`,
          `customer_contacts` and `sites`, so the screen reflects permission
          rather than deciding it. A permission change needs no code change. */}
      <Route
        path="/customers"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><CustomersPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />
      <Route
        path="/customers/:customerId"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><CustomerDetailPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      {/* Job statuses and categories. Owner or admin, matching every other
          configuration screen: these define vocabulary the whole organisation
          then works inside. */}
      <Route
        path="/settings/job-statuses"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><JobStatusesPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      {/* M16C. Scheduler configuration, so the same owner and administrator
          guard as every other configuration screen. No permission is widened:
          a member reads the labels through RLS in order to understand a block,
          and only an administrator writes them. */}
      <Route
        path="/settings/outcome-reasons"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><OutcomeReasonsPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/job-categories"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><JobCategoriesPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Organisation settings. Owner only, and the guard mirrors the policy
          rather than inventing one: `organisations` carries an owner-only
          UPDATE policy, because an admin may run the workshop but changing the
          pay week regroups every historical report. The page is readable at
          this level too, and the financial field inside is decided by RLS. */}
      <Route
        path="/settings/organisation"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><OrganisationSettingsPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Costing rules. Owner or admin, matching admins_can_manage_* on both
          rule tables. Every member may SELECT them, because they are rules
          rather than wage data and the engine needs them to compute hours, but
          nothing on these screens is worth showing to somebody who cannot act
          on it. */}
      <Route
        path="/settings/overtime"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><OvertimeRulesPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><BillingRulesPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/public-holidays"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><PublicHolidaysPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* Wage matrix, configuration history and readiness. Owner or admin, the
          same as the rest of settings.

          The wage matrix shows amounts, so it also sits behind
          RequireFinancialAccess. That is belt and braces rather than the
          boundary: owners and admins hold financial access implicitly (ADR-003)
          so the guard admits everyone the role guard already admitted, and RLS
          on employee_pay_rates is what actually withholds the rows. The page
          handles being given none, and says the amounts are hidden rather than
          rendering blanks. */}
      <Route
        path="/settings/wage-matrix"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireFinancialAccess>
                <RequireActiveOrganisation>
                  <AuthenticatedShell>
                    <Lazy><WageMatrixPage /></Lazy>
                  </AuthenticatedShell>
                </RequireActiveOrganisation>
              </RequireFinancialAccess>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/audit-history"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><AuditHistoryPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/readiness"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ReadinessPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* /settings itself is the organisation page, so an old bookmark and the
          navigation land in the same place. */}
      <Route path="/settings" element={<Navigate to="/settings/organisation" replace />} />

      {/* Timesheet import. Owner or admin, matching the storage policies on the
          private bucket, which admit an administrator of the organisation named
          in the object path and nobody else. A viewer with financial access is
          still refused: seeing money is not the same permission as changing
          where the money comes from.

          The batch id in the mapping route is an identifier, never authority.
          The repository reads the batch through RLS, so a batch belonging to
          another tenant returns nothing and the screen reports a refusal. The
          organisation is taken from the session's resolved membership and is
          never accepted from the URL or a query string. */}
      {/* Time that actually happened, typed rather than imported.

          Guarded like the operational job screens rather than like the import:
          a member reads the week, and the owner-or-admin policies on
          timesheet_entries are what refuse a write. The page offers no Add
          time button to anybody who cannot use it, which is courtesy; the
          policy is what enforces it.

          Not owner-or-admin at the route, because a viewer reading hours is
          the same permission they already have on /jobs, and refusing them the
          route would hide operational information they are entitled to.

          There is no self-service route here. A worker entering their own time
          needs Swamito to know which employee they are, and no relationship
          between a user and an employee exists in this schema (ADR-108). */}
      <Route
        path="/timesheets"
        element={
          <RequireAuth>
            <RequireActiveOrganisation>
              <AuthenticatedShell>
                <Lazy><TimesheetsPage /></Lazy>
              </AuthenticatedShell>
            </RequireActiveOrganisation>
          </RequireAuth>
        }
      />

      <Route
        path="/imports"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ImportBatchesPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/imports/new"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ImportUploadPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/imports/:batchId/mapping"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ImportMappingPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/imports/:batchId/review"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ImportReviewPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/imports/:batchId/confirm"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><ImportConfirmPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      {/* Data migration. Owner or admin, matching the migration tables, which
          have an admin-manage policy and no viewer policy at all (ADR-100).
          The batch id in the URL is an identifier, never authority: the
          repository reads through RLS, so a migration belonging to another
          tenant returns nothing and the screen says it was not found. */}
      <Route
        path="/migrations"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><MigrationsPage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/migrations/:batchId"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireActiveOrganisation>
                <AuthenticatedShell>
                  <Lazy><MigrationWorkspacePage /></Lazy>
                </AuthenticatedShell>
              </RequireActiveOrganisation>
            </RequireRole>
          </RequireAuth>
        }
      />
      {/* Labour cost. Owner or admin, and behind RequireFinancialAccess.

          Both guards are deliberate rather than redundant. The role guard
          matches every other administration screen. The financial guard
          matches ADR-004: wage-derived figures are not sent to a browser that
          may not see them, and RLS on employee_pay_rates is what actually
          withholds the rows. Owners and admins hold financial access
          implicitly (ADR-003), so in practice the second guard admits everyone
          the first did; it is there so a future permission change is a
          configuration change rather than a code change. */}
      <Route
        path="/costing"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireFinancialAccess>
                <RequireActiveOrganisation>
                  <AuthenticatedShell>
                    <Lazy><CostingPage /></Lazy>
                  </AuthenticatedShell>
                </RequireActiveOrganisation>
              </RequireFinancialAccess>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/costing/:entryId"
        element={
          <RequireAuth>
            <RequireRole allow={['owner', 'admin']}>
              <RequireFinancialAccess>
                <RequireActiveOrganisation>
                  <AuthenticatedShell>
                    <Lazy><CostingDetailPage /></Lazy>
                  </AuthenticatedShell>
                </RequireActiveOrganisation>
              </RequireFinancialAccess>
            </RequireRole>
          </RequireAuth>
        }
      />

      {/* The M6 navigation pointed at /import while the screens did not exist.
          Kept so that link, and any bookmark of it, still lands somewhere real. */}
      <Route path="/import" element={<Navigate to="/imports" replace />} />

      {/* Platform administration. Not a tenant role, so it is checked on its own
          rather than through membership. */}
      <Route
        path="/platform/organisations"
        element={
          <RequirePlatformAdmin>
            <AuthenticatedShell>
              <Lazy><PlatformOrganisationsPage /></Lazy>
            </AuthenticatedShell>
          </RequirePlatformAdmin>
        }
      />

      <Route path="/denied" element={<PermissionDeniedPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function AppRouter() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </SessionProvider>
  )
}
